import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { Redirect, Switch, useLocation } from 'react-router-dom'
import { Route } from 'react-router'
import { Channel, ConsumerMetaTags, useAppContext, withLayout } from '@at/sauron-platform-core'
import { getChannelCapitalised } from '@at/formatter-utils/lib/channel-utils/channel.util'
import { getSearchResultsRoot } from './utils/channel-utilities'
import { LEASING_SEARCH_PATH, RETAILER_STOCK_SEARCH_PATH, SEARCH_RESULTS_PATH } from './constants/route-path'
import SearchQueryPage from './pages/search-results/search-query-page'
import { SkeletonSearchResultsPageWithLayout } from './pages/search-results/search-results.skeleton'
import { Footer } from './components/footer/footer'
import { SearchPage } from './forms/hocs/search-page.context'
import { Header } from './header'
import { HeaderProps } from '@at/design-system-collections'
import { Platform } from '@snowplow/browser-tracker-core'
import { SearchResultsAppContextProvider } from './contexts/app-context-provider'
import { ATTracking, CURRENT_PAGE_ENTITY_VERSION, useATPageViewEvent } from '@at/consumer-web-snowplow'
import { useSearchViewState } from './contexts/search-view-state-context'
import { ATDSGlobalStyles } from '@at/design-system-foundation'
import { AppGlobalStyles } from './app.styles'
import { SessionKeys } from './utils/session-keys'
import { uuidProvider } from './pages/search-results/uuid-provider'
import { useRetailerStockSearch } from './utils/retailer-stock'

import { mapSearchData, pageName } from './utils/tracking/search-filter-context'
import { getPageNumberQueryParam } from './utils/uri.utilities'
import { useChannel } from './hooks/use-abtest.hook'
import { useFirstRender } from './hooks/use-first-render/use-first-render'
import { FORCE_LOAD_RESULTS_FROM_CACHE } from './graphql/cache'
import { BuyingJourney106, Entity } from '@at/track-types'
import { initNewTracker } from '@at/track-core'
import { removeMatchingEntities } from './utils/tracking/tracking'
import { searchEntity } from './utils/tracking/search'
import { setSearchId } from './utils/set-search-id'
import { useIsLeasingSearch } from './utils/is-leasing-search'
import { ErrorBoundary } from '@at/design-system-patterns'
import { getIsNewVehicleSearch } from './utils/tracking/is-new-vehicle-search'
import { Helmet } from 'react-helmet-async'
import { AiSearchPageWithLayout } from './pages/ai-search/ai-search'

const HeaderWithFilterToggle: FC<HeaderProps> = (props: HeaderProps) => {
    const { toggleSearchFilter } = useSearchViewState()
    return (
        <Header
            {...props}
            searchOnClick={() => {
                toggleSearchFilter(true)
            }}
        />
    )
}

const SearchResultsPage: FC<Record<string, unknown>> = () => {
    return (
        <ErrorBoundary>
            <SearchPage isSkeletonPage={false}>
                <SearchQueryPage />
            </SearchPage>
        </ErrorBoundary>
    )
}

const SearchResultsPageWithLayout = withLayout(SearchResultsPage, {
    HeaderComponent: HeaderWithFilterToggle,
    FooterComponent: Footer,
    mainClassName: 'search-results-container',
})

const SearchResultsPageWithoutLayout = withLayout(SearchResultsPage, {
    HeaderComponent: () => null,
    FooterComponent: () => null,
    mainClassName: 'search-results-container',
})

export const setSearchIdIfFirstRender = (isFirstRender: boolean, search: string) => {
    const hasReturnedFromFPA = search.includes(`${FORCE_LOAD_RESULTS_FROM_CACHE}=1`)
    if ((isFirstRender && !hasReturnedFromFPA) || !sessionStorage.getItem(SessionKeys.SEARCH_ID)) {
        setSearchId()
    }
}

const PageTrack = ({ children }: { children: ReactNode }) => {
    const { showSearchFilter } = useSearchViewState()
    const { search } = useLocation()
    const location = useLocation()
    const channel = useChannel()
    const [isRetailerStockSearch] = useRetailerStockSearch()
    const isLeasingSearch = useIsLeasingSearch()

    const pageNumber = getPageNumberQueryParam(location)
    const searchId = sessionStorage.getItem(SessionKeys.SEARCH_ID) ?? uuidProvider.getUUID()

    const searchContext: Entity = useMemo(() => searchEntity(searchId, pageNumber), [searchId, pageNumber])

    const { firePageView } = useATPageViewEvent()

    useEffect(() => {
        const canFirePageViewEvent =
            !showSearchFilter &&
            !search.includes('showSaveSearch=true') &&
            !search.includes('showSavedSearches=true') &&
            sessionStorage.getItem(SessionKeys.SEARCH_ID)

        // this ensures we do not fire a pageview when any filter is changed in the mobile search form
        if (canFirePageViewEvent) {
            const getMappedSearchData = mapSearchData(search)
            firePageView({
                pageName: pageName(channel, isRetailerStockSearch, isLeasingSearch),
                channel,
                // Callback to allow user to modify the entities
                customiseEntities: (contextEntities) => {
                    const filteredEntities = removeMatchingEntities(
                        [getMappedSearchData, searchContext],
                        contextEntities
                    )
                    return [...contextEntities, ...filteredEntities]
                },
            })
        }
    }, [channel, search, searchContext, firePageView, isRetailerStockSearch, isLeasingSearch, showSearchFilter])

    return <>{children}</>
}

const SearchResultsWithPageTracking = () => {
    const isFirstRender = useFirstRender()
    const { search } = useLocation()
    setSearchIdIfFirstRender(isFirstRender, search)
    const {
        config: { appName },
        ssrMode,
    } = useAppContext()
    const channel = useChannel()

    const location = useLocation()
    const pageNumber = getPageNumberQueryParam(location)
    const noLayout = location.search.includes('nolayout=true')
    const searchId = sessionStorage.getItem(SessionKeys.SEARCH_ID) ?? uuidProvider.getUUID()
    const isLeasingSearch = useIsLeasingSearch()
    const isNewVehicleSearch = getIsNewVehicleSearch(search, channel)

    const searchContext: Entity = useMemo(() => {
        return {
            SCHEMA_KEY: 'Search100',
            search_id: searchId,
            page_number: pageNumber,
        }
    }, [searchId, pageNumber])

    const getBuyingJourneyEntity = (product: BuyingJourney106['product']): BuyingJourney106 => {
        return {
            SCHEMA_KEY: 'BuyingJourney106',
            product,
            view_type: 'search',
        }
    }

    return (
        <ATTracking
            pageEntity={{
                SCHEMA_KEY: CURRENT_PAGE_ENTITY_VERSION,
                name: appName,
                channel,
            }}
            customiseEntities={(storedEntities) => {
                const entities = [mapSearchData(search), searchContext, ...storedEntities]

                if (isLeasingSearch || isNewVehicleSearch) {
                    entities.push(getBuyingJourneyEntity(isLeasingSearch ? 'leasing' : 'new'))
                }
                return entities
            }}
            skipTracking={ssrMode}
        >
            <PageTrack>{noLayout ? <SearchResultsPageWithoutLayout /> : <SearchResultsPageWithLayout />}</PageTrack>
        </ATTracking>
    )
}

const SearchResultPageMetaTags = ({
    title,
    description,
    children,
}: {
    title: string
    description?: string
    children?: ReactNode
}) => (
    <>
        <ConsumerMetaTags title={title} description={description || ''} canonicalUrl="" />
        <Helmet>{children}</Helmet>
    </>
)

const Routing: FC<{
    SearchResultsPage: FC<Record<string, unknown>>
}> = ({ SearchResultsPage }) => {
    const { search, hash } = useLocation()
    const channel = useChannel()

    const autoTraderUK = '- Autotrader UK'
    const title = (function () {
        switch (channel) {
            case Channel.cycles: {
                return `Electric bikes for Sale ${autoTraderUK}`
            }
            case Channel.cars:
            case Channel.vans: {
                return `New & Used ${getChannelCapitalised(channel)} for Sale ${autoTraderUK}`
            }
            default: {
                return `Used ${getChannelCapitalised(channel)} for Sale ${autoTraderUK}`
            }
        }
    })()

    return (
        <Switch>
            <Route exact path={SEARCH_RESULTS_PATH}>
                <SearchResultPageMetaTags title={title}>
                    <meta name="robots" content="follow,noindex" />
                    <meta name="referrer" content="origin" />
                    <meta name="channel" content={channel} />
                    <meta name="gpt1" content={`/323304435/web/${channel}/search/listings`} />
                </SearchResultPageMetaTags>
                <SearchResultsPage />
            </Route>
            <Route exact path="/electric-bike-search">
                <SearchResultPageMetaTags title={title}>
                    <meta name="robots" content="follow,noindex" />
                    <meta name="referrer" content="origin" />
                    <meta name="channel" content={Channel.cycles} />
                    <meta name="gpt1" content={`/323304435/web/cycles/search/listings`} />
                </SearchResultPageMetaTags>
                <SearchResultsPage />
            </Route>
            <Route exact path={RETAILER_STOCK_SEARCH_PATH}>
                <SearchResultPageMetaTags title={title}>
                    <meta name="robots" content="follow,noindex" />
                    <meta name="referrer" content="origin" />
                    <meta name="channel" content={channel} />
                    <meta name="gpt1" content={`/323304435/web/${channel}/search/listings`} />
                </SearchResultPageMetaTags>
                <SearchResultsPage />
            </Route>
            <Route exact path="electric-bike/retailer/stock">
                <SearchResultPageMetaTags title={title}>
                    <meta name="robots" content="follow,noindex" />
                    <meta name="referrer" content="origin" />
                    <meta name="channel" content={Channel.cycles} />
                    <meta name="gpt1" content={`/323304435/web/cycles/search/listings`} />
                </SearchResultPageMetaTags>
                <SearchResultsPage />
            </Route>
            <Route exact path={LEASING_SEARCH_PATH}>
                <SearchResultPageMetaTags title={`${getChannelCapitalised(channel)} leasing deals ${autoTraderUK}`}>
                    <meta name="robots" content="follow,noindex" />
                    <meta name="referrer" content="origin" />
                    <meta name="channel" content={Channel.cars} />
                    <meta name="gpt1" content={`/323304435/web/${channel}/search/leasing/listings`} />
                </SearchResultPageMetaTags>
                <SearchResultsPage />
            </Route>
            <Route exact path="/car-search/ai">
                <SearchResultPageMetaTags title={title}>
                    <meta name="robots" content="nofollow,noindex" />
                    <meta name="referrer" content="origin" />
                    <meta name="channel" content={channel} />
                    <meta name="gpt1" content={`/323304435/web/${channel}/search/listings`} />
                </SearchResultPageMetaTags>
                <AiSearchPageWithLayout />
            </Route>
            <Redirect from="/search" to={{ pathname: '/car-search', search, hash }} />
            <Redirect from="/" to={{ pathname: `/${getSearchResultsRoot(channel)}`, search, hash }} />
        </Switch>
    )
}

const initialiseTracker = (isDesktop: () => boolean, env: string, appName: string, ssrMode: boolean) => {
    const platform: Platform = isDesktop() ? 'web' : 'mob'
    initNewTracker({ env, appName, platform, ssrMode })
}

const SkeletonRouting = () => <Routing SearchResultsPage={SkeletonSearchResultsPageWithLayout} />

const ServerMain = () => {
    return (
        <SearchResultsAppContextProvider>
            <ATDSGlobalStyles />
            <AppGlobalStyles />
            <SkeletonRouting />
        </SearchResultsAppContextProvider>
    )
}

const Main = () => {
    const {
        isDesktop,
        config: { env, appName },
        ssrMode,
    } = useAppContext()

    const [isClient, setIsClient] = useState(false)
    useEffect(() => {
        setIsClient(true)
    }, [])

    if (isClient) {
        initialiseTracker(isDesktop, env, appName, ssrMode)

        return (
            <SearchResultsAppContextProvider>
                <ATDSGlobalStyles />
                <AppGlobalStyles />
                <Routing SearchResultsPage={SearchResultsWithPageTracking} />
            </SearchResultsAppContextProvider>
        )
    }

    return <ServerMain />
}

export default Main
